import React, { useState, useEffect, useRef } from 'react';
import styles from './FortuneSpin.module.scss';
import { mockPrizes } from './mockData';
import { initAudio, playTick, playWin, startSpinLoop, stopSpinLoop } from './audioEngine';
import itemBronze from 'assets/item-bronze.webp';
import itemSilver from 'assets/item-silver.webp';
import itemGold from 'assets/item-gold.webp';
import itemPlatinum from 'assets/item-platinum.webp';
import itemDiamond from 'assets/item-diamond.webp';
import itemNebula from 'assets/item-nebula.webp';

interface FortuneSpinProps {
  items: any[];
  isSpinning: boolean;
  winningId: number | null;
  stoppedId?: number | null;
  onSpinEnd?: () => void;
  levelName?: string;
}

const FortuneSpin: React.FC<FortuneSpinProps> = ({
  items,
  isSpinning,
  winningId,
  onSpinEnd,
  levelName,
}) => {
  const [showWinner, setShowWinner] = useState(false);

  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>(0);
  const onSpinEndRef = useRef(onSpinEnd);

  useEffect(() => {
    onSpinEndRef.current = onSpinEnd;
  }, [onSpinEnd]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const itemWidth = isMobile ? 90 : 120;
  const gap = 16;
  const totalItemWidth = itemWidth + gap;
  const blocksCount = 30;

  const lastTickItemRef = useRef<number>(-1);

  const mappedItems = items.map((item, idx) => {
    const visual = mockPrizes[idx % mockPrizes.length];
    return {
      ...item,
      color: visual.color,
      icon: visual.icon,
    };
  });

  const getLevelImage = () => {
    if (!levelName) return itemBronze;
    const name = levelName.toLowerCase();
    if (name.includes('bronze')) return itemBronze;
    if (name.includes('silver')) return itemSilver;
    if (name.includes('gold')) return itemGold;
    if (name.includes('platinum')) return itemPlatinum;
    if (name.includes('diamond')) return itemDiamond;
    if (name.includes('nebula')) return itemNebula;
    return itemBronze;
  };

  const currentItemImage = getLevelImage();

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    if (isSpinning && !winningId) {
      initAudio();

      setShowWinner(false); // eslint-disable-line react-hooks/set-state-in-effect
      lastTickItemRef.current = -1;
      startSpinLoop();

      const computedStyle = window.getComputedStyle(track);
      const matrix = new (window.WebKitCSSMatrix || (window as any).MSCSSMatrix)(
        computedStyle.transform,
      );
      const currentX = matrix.m41 || 0;

      track.classList.remove(styles.idleScroll);
      track.style.animation = 'none';
      track.style.transition = 'none';
      track.style.transform = `translateX(${currentX}px)`;

      void track.offsetWidth;

      track.style.transition = 'transform 20s linear';
      track.style.transform = `translateX(${currentX - 40000}px)`;

      const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;

      const checkFrame = () => {
        if (!trackRef.current) return;
        const compStyle = window.getComputedStyle(trackRef.current);
        const mat = new (window.WebKitCSSMatrix || (window as any).MSCSSMatrix)(
          compStyle.transform,
        );
        const curX = mat.m41;
        const centerPosOnTrack = containerWidth / 2 - curX;
        const currentItemPassed = Math.floor(centerPosOnTrack / totalItemWidth);

        if (currentItemPassed !== lastTickItemRef.current && currentItemPassed > 0) {
          playTick();
          lastTickItemRef.current = currentItemPassed;
        }
        animationRef.current = requestAnimationFrame(checkFrame);
      };
      animationRef.current = requestAnimationFrame(checkFrame);
    } else if (isSpinning && winningId) {
      const computedStyle = window.getComputedStyle(track);
      const matrix = new (window.WebKitCSSMatrix || (window as any).MSCSSMatrix)(
        computedStyle.transform,
      );
      const currentX = matrix.m41 || 0;

      track.style.transition = 'none';
      track.style.transform = `translateX(${currentX}px)`;

      void track.offsetWidth;

      const itemIndex = mappedItems.findIndex((item) => Number(item.id) === Number(winningId));

      if (itemIndex !== -1) {
        const stopBlockIdx = 15;
        const targetItemGlobalIdx = stopBlockIdx * mappedItems.length + itemIndex;

        const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
        const centerScreen = containerWidth / 2;

        const winnerPosInTrack = targetItemGlobalIdx * totalItemWidth + itemWidth / 2;
        const finalOffset = centerScreen - winnerPosInTrack;

        track.style.transition = 'transform 8s cubic-bezier(0.1, 0, 0, 1)';
        track.style.transform = `translateX(${finalOffset}px)`;

        let innerTimer: NodeJS.Timeout;
        const timer = setTimeout(() => {
          if (animationRef.current) cancelAnimationFrame(animationRef.current);
          stopSpinLoop();
          playWin();
          setShowWinner(true);

          innerTimer = setTimeout(() => {
            onSpinEndRef.current?.();
          }, 3600);
        }, 8000);

        return () => {
          clearTimeout(timer);
          if (innerTimer) clearTimeout(innerTimer);
        };
      }
    } else {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      stopSpinLoop();

      track.style.transition = 'none';
      track.style.transform = '';
      track.style.animation = '';
      track.classList.add(styles.idleScroll);
    }
  }, [isSpinning, winningId, mappedItems.length, totalItemWidth, itemWidth]);

  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      stopSpinLoop(true);
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.carouselWindow} ref={containerRef}>
        <div
          className={styles.track}
          ref={trackRef}
          style={
            {
              gap: `${gap}px`,
              '--idle-width': `-${mappedItems.length * totalItemWidth}px`,
            } as React.CSSProperties
          }
        >
          {Array.from({ length: blocksCount }).map((_, blockIdx) => (
            <div key={blockIdx} className={styles.itemGroup} style={{ gap: `${gap}px` }}>
              {mappedItems.map((item, idx) => {
                const isWinner =
                  showWinner &&
                  isSpinning &&
                  !!winningId &&
                  blockIdx === 15 &&
                  Number(item.id) === Number(winningId);

                return (
                  <div
                    key={`${blockIdx}-${item.id}-${idx}`}
                    className={`${styles.card} ${isWinner ? styles.winner : ''}`}
                    style={{
                      backgroundImage: `url(${currentItemImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    {/* <div className={styles.icon}>{item.icon}</div> */}
                    <div className={styles.amount}>${item.amount}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className={styles.centerMarker}>
          <div className={styles.arrowDown} />
          <div className={styles.arrowUp} />
        </div>
      </div>
    </div>
  );
};

export default FortuneSpin;
